import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';

export const selectClass =
  'h-8 border border-border bg-background px-2.5 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20';

/** The 24 legacy travel cari types (tblcaritip order: customers, suppliers, internal). */
export const TRAVEL_CARI_TYPES = [
  'M', 'W', 'AH', 'E',
  'O', 'S', 'T', 'H', 'D', 'I', 'G', 'RC', 'R', 'V', 'C', 'Q', 'CS',
  'P', 'ORT', 'L', 'A101', 'V103', 'X', 'X103',
] as const;

export function useTravelCariTypes() {
  const { data } = trpc.travelCariType.list.useQuery({ includeInactive: true });
  return [...new Set([...TRAVEL_CARI_TYPES, ...(data ?? []).map(type => type.code)])];
}

export const CURRENCIES = ['TRY', 'EUR', 'GBP', 'USD'] as const;

export const CURRENCY_NAMES: Record<string, string> = {
  TRY: 'TÜRK LİRASI - TL',
  EUR: 'EURO - EUR',
  GBP: 'İNGİLİZ STERLİNİ - GBP',
  USD: 'AMERİKAN DOLARI - USD',
};

export const fmtMoney = (n: number): string =>
  new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

/** Translated label for a cari type code (falls back to the code itself). */
export function useCariTypeLabel(): (code: string) => string {
  const { t, i18n } = useTranslation('accounting');
  const { data } = trpc.travelCariType.list.useQuery({ includeInactive: true });
  return (code: string) => {
    const type = data?.find(row => row.code === code);
    if (type) return i18n.language.startsWith('en') && type.nameEn ? type.nameEn : type.name;
    const label = t(`cari.types.${code}`, code);
    return label === code ? code : label;
  };
}

/**
 * Fiscal years present in the travel cari subledger + selection state.
 * Each legacy fiscal year is self-contained (opening + that year's movements),
 * so every view works within one selected year. Defaults to the newest year.
 */
export function useFiscalYear() {
  const { data: years } = trpc.travelCari.fiscalYears.useQuery();
  const [fiscalYear, setFiscalYear] = useState<number | null>(null);

  useEffect(() => {
    const newest = years?.[0];
    if (fiscalYear === null && newest !== undefined) setFiscalYear(newest);
  }, [years, fiscalYear]);

  return { years: years ?? [], fiscalYear, setFiscalYear };
}
